import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LockIcon from '@material-ui/icons/Lock';
import { useChangePassword } from '../../../../hooks/useChangePassword';
import { trackPasswordChangePageView } from '../../../../helpers/analytics';
import RecaptchaBadge from '../../../../components/RecaptchaBadge';

const useStyles = makeStyles((theme) => ({
  root: {},
  cardHeader: {
    paddingBottom: theme.spacing(2)
  },
  cardContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  textField: {
    marginTop: theme.spacing(2),
    '&:first-child': {
      marginTop: 0
    }
  },
  cardActions: {
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2)
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.success.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    color: theme.palette.success.dark
  },
  requirements: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius
  },
  requirementItem: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  },
  lockIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
    verticalAlign: 'middle'
  }
}));

const Password = (props) => {
  const { className, ...rest } = props;
  const classes = useStyles();

  const {
    formState,
    handleChange,
    handleSubmit,
    hasError,
    isLoading,
    isSuccess
  } = useChangePassword();

  useEffect(() => {
    // Track page view on mount
    trackPasswordChangePageView();
  }, []);

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <form onSubmit={handleSubmit}>
        <CardHeader
          className={classes.cardHeader}
          subheader="Update your password to keep your account secure"
          title={
            <>
              <LockIcon className={classes.lockIcon} />
              Password
            </>
          }
        />
        <Divider />
        <CardContent className={classes.cardContent}>
          {isSuccess && (
            <Box className={classes.successMessage}>
              <CheckCircleIcon />
              <Typography variant="body2">
                Your password has been successfully changed!
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Current Password"
            name="currentPassword"
            onChange={handleChange}
            type="password"
            value={formState.values.currentPassword || ''}
            variant="outlined"
            className={classes.textField}
            error={hasError('currentPassword')}
            helperText={
              hasError('currentPassword')
                ? formState.errors.currentPassword[0]
                : 'Enter your current password'
            }
            disabled={isLoading || isSuccess}
            data-test="current-password-input"
          />

          <TextField
            fullWidth
            label="New Password"
            name="newPassword"
            onChange={handleChange}
            type="password"
            value={formState.values.newPassword || ''}
            variant="outlined"
            className={classes.textField}
            error={hasError('newPassword')}
            helperText={
              hasError('newPassword')
                ? formState.errors.newPassword[0]
                : 'Must be at least 8 characters'
            }
            disabled={isLoading || isSuccess}
            data-test="new-password-input"
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            name="confirmPassword"
            onChange={handleChange}
            type="password"
            value={formState.values.confirmPassword || ''}
            variant="outlined"
            className={classes.textField}
            error={hasError('confirmPassword')}
            helperText={
              hasError('confirmPassword')
                ? formState.errors.confirmPassword[0]
                : 'Re-enter your new password'
            }
            disabled={isLoading || isSuccess}
            data-test="confirm-password-input"
          />

          <Box className={classes.requirements}>
            <Typography variant="subtitle2" gutterBottom>
              Password Requirements:
            </Typography>
            <Typography variant="body2" className={classes.requirementItem}>
              • Minimum 8 characters
            </Typography>
            <Typography variant="body2" className={classes.requirementItem}>
              • Must be different from your current password
            </Typography>
            <Typography variant="body2" className={classes.requirementItem}>
              • Both new password fields must match
            </Typography>
          </Box>

          <RecaptchaBadge />
        </CardContent>
        <Divider />
        <CardActions className={classes.cardActions}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={!formState.isValid || isLoading || isSuccess}
            data-test="update-password-button">
            {isLoading ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                Updating...
              </>
            ) : (
              'Update Password'
            )}
          </Button>
        </CardActions>
      </form>
    </Card>
  );
};

Password.propTypes = {
  className: PropTypes.string
};

export default Password;
